import { Component, useEffect, useState } from "react";
import { useAppState } from "./hooks/useAppState";
import { APP_INFO, normalizeNativeInfo, recordVersionSnapshot } from "./constants/appInfo";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    this.props.onError("❌ [RenderError] " + error.message);
    this.props.onError("→ " + (info.componentStack?.trim().split("\n")[1]?.trim() || "unknown component"));
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "40px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: "#f87171", marginBottom: 12 }}>
            ❌ Something crashed: <code style={{ fontSize: 12 }}>{this.state.error.message}</code>
          </div>
          <button
            onClick={() => { this.setState({ error: null }); this.props.onNavigate("Admin"); }}
            style={{ fontSize: 13, color: "#f97316", background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)", borderRadius: 8, padding: "8px 18px", cursor: "pointer" }}
          >
            View error in Admin →
          </button>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ fontSize: 13, color: "#aaa", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "8px 18px", cursor: "pointer", marginLeft: 10 }}
          >
            Dismiss
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { VIEWS } from "./constants/config";
import { getTodayDay, getTodayDate } from "./utils/formatting";
import { apiFetch } from "./utils/apiClient";
import AuthGate  from "./components/AuthGate";
import Dashboard  from "./views/Dashboard";
import MealLog    from "./views/MealLog";
import Supply     from "./views/Supply";
import History    from "./views/History";
import Progress   from "./views/Progress";
import Fitness    from "./views/Fitness";
import Foods      from "./views/Foods";
import Profile    from "./views/Profile";
import Admin      from "./views/Admin";

export default function App() {
  const state = useAppState();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [nativeInfo, setNativeInfo] = useState(() => (
    typeof window !== "undefined" ? normalizeNativeInfo(window.__SUBLIME_NATIVE_INFO__) : null
  ));
  const [isNativeShell, setIsNativeShell] = useState(() => (
    typeof window !== "undefined" && Boolean(window.__SUBLIME_NATIVE__)
  ));
  const [watchSyncStatus, setWatchSyncStatus] = useState({ status: "idle", message: "" });
  const [watchDiagnostics, setWatchDiagnostics] = useState({ open: false, loading: false, data: null, error: "" });
  const {
    dataLoading, dataLoadError, saveStatus, retryFailedWrites,
    view, setView,
    logs, saveLogs, sortedLogs, getDayTotals,
    activeMeal, setActiveMeal, logDate, setLogDate, liveMacros,
    groceries, saveGroceries,
    macroGoals, saveMacroGoals,
    fitnessGoals, saveFitnessGoals,
    editFitnessGoals, setEditFitnessGoals,
    editMacroGoals, setEditMacroGoals,
    visibleGoalCards, setVisibleGoalCards,
    workouts, saveWorkouts,
    customFoods, saveCustomFoods,
    weightLog, healthConnect, todayWeighIn, setTodayWeighIn, saveWeightEntry,
    progressTab, setProgressTab,
    parsingWorkout, setParsing2,
    toast, showToast,
    parsing, setParsing,
    uploadProgress, setUploadProgress,
    parseErr, setParseErr,
    apiUsage, saveApiUsage,
    debugLog, setDebugLog, addLog,
    showDebug, setShowDebug,
    chartMetric, setChartMetric,
    timeRange, setTimeRange,
    mealFileRef,
    forceMigrate,
    systemAlerts, dismissAlert, clearAlerts,
    authRequired, unlockApi, logoutApi,
    refreshRemoteState,
  } = state;

  const todayDay  = getTodayDay();
  const todayDate = getTodayDate();
  const navItems = isNativeShell ? ["Watch Sync", ...VIEWS] : VIEWS;

  const shared = {
    logs, saveLogs, sortedLogs, getDayTotals,
    activeMeal, setActiveMeal, logDate, setLogDate, liveMacros,
    logDay: todayDay,
    groceries, saveGroceries,
    macroGoals, saveMacroGoals,
    fitnessGoals, saveFitnessGoals,
    editFitnessGoals, setEditFitnessGoals,
    editMacroGoals, setEditMacroGoals,
    visibleGoalCards, setVisibleGoalCards,
    workouts, saveWorkouts,
    customFoods, saveCustomFoods,
    weightLog, healthConnect, todayWeighIn, setTodayWeighIn, saveWeightEntry,
    progressTab, setProgressTab,
    parsingWorkout, setParsing2,
    parsing, setParsing,
    uploadProgress, setUploadProgress,
    parseErr, setParseErr,
    apiUsage, saveApiUsage,
    debugLog, setDebugLog, addLog,
    mealFileRef,
    showToast, setView,
    todayDay, todayDate,
    chartMetric, setChartMetric,
    timeRange, setTimeRange,
    forceMigrate,
    systemAlerts, dismissAlert, clearAlerts,
    logoutApi,
    appInfo: { ...APP_INFO, isNativeShell, nativeInfo, watchSyncStatus },
  };

  useEffect(() => {
    function applyNativeInfo(detail) {
      const info = normalizeNativeInfo(detail?.nativeInfo || detail || window.__SUBLIME_NATIVE_INFO__);
      if (info) setNativeInfo(info);
      if (window.__SUBLIME_NATIVE__ || info) setIsNativeShell(true);
    }

    function markNativeShell(event) {
      applyNativeInfo(event?.detail);
    }

    function handleWatchSyncStatus(event) {
      const detail = event.detail || {};
      applyNativeInfo(detail);
      setWatchSyncStatus({
        status: detail.status || "idle",
        message: detail.message || "",
        summary: detail.summary || null,
      });
      if (detail.status === "ready") {
        refreshRemoteState().catch(() => {});
      }
      if (detail.message && detail.status !== "syncing") {
        showToast(detail.message);
      }
    }

    markNativeShell();
    window.addEventListener("sublime:native-ready", markNativeShell);
    window.addEventListener("sublime:watch-sync-status", handleWatchSyncStatus);
    return () => {
      window.removeEventListener("sublime:native-ready", markNativeShell);
      window.removeEventListener("sublime:watch-sync-status", handleWatchSyncStatus);
    };
  }, [refreshRemoteState, showToast]);

  useEffect(() => {
    recordVersionSnapshot(isNativeShell ? nativeInfo : null);
  }, [isNativeShell, nativeInfo]);

  function requestWatchSync() {
    if (window.sublimeNativeSync) {
      setWatchSyncStatus({ status: "syncing", message: "Syncing watch data" });
      window.sublimeNativeSync();
    } else {
      showToast("Open Sublime Android app to sync watch");
    }
  }

  async function openWatchDiagnostics() {
    setWatchDiagnostics({ open: true, loading: true, data: null, error: "" });
    try {
      const response = await apiFetch(`/api/health?date=${todayDate}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) throw new Error(data.error || `Diagnostics failed (${response.status})`);
      setWatchDiagnostics({ open: true, loading: false, data, error: "" });
    } catch (error) {
      setWatchDiagnostics({ open: true, loading: false, data: null, error: error.message || "Diagnostics failed" });
    }
  }

  function closeWatchDiagnostics() {
    setWatchDiagnostics(current => ({ ...current, open: false }));
  }

  if (authRequired) return <AuthGate onUnlock={unlockApi} />;

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg,#050508 0%,#070710 50%,#050508 100%)",
      color: "#e2e8f0",
      fontFamily: "'DM Sans',sans-serif",
    }}>
      {/* Fonts */}
      <style>{`
        @font-face {
          font-family: 'Arexa';
          src: url('/fonts/ArexaDemo-Regular.otf') format('opentype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Space+Mono:wght@400;700&display=swap');
        * { box-sizing: border-box; }
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
        .nav-scroll::-webkit-scrollbar { display: none; }
        .nav-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        @media (max-width: 600px) {
          .main-pad { padding: calc(22px + env(safe-area-inset-top)) 18px calc(88px + env(safe-area-inset-bottom)) !important; }
          .page-title { font-size: 20px !important; }
          .hide-mobile { display: none !important; }
          .grid-auto { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* ── Floating sidebar toggle — sticks to right edge ── */}
      <button
        onClick={() => setSidebarOpen(o => !o)}
        style={{
          position: "fixed", right: 8, top: "50%", transform: "translateY(-50%)",
          zIndex: 70, cursor: "pointer",
          background: sidebarOpen ? "rgba(249,115,22,0.85)" : "rgba(12,12,20,0.92)",
          border: "1px solid " + (sidebarOpen ? "rgba(249,115,22,0.6)" : "rgba(255,255,255,0.1)"),
          borderRadius: 10,
          padding: "16px 9px", display: "flex", flexDirection: "column", gap: 5,
          alignItems: "center", backdropFilter: "blur(12px)",
          boxShadow: "-2px 0 12px rgba(0,0,0,0.4)",
        }}
      >
        <span style={{ display:"block", width:16, height:2, background: sidebarOpen ? "#fff" : "#aaa", borderRadius:2, transition:"all 0.2s", transform: sidebarOpen ? "rotate(45deg) translate(3px,3px)" : "none" }}/>
        <span style={{ display:"block", width:16, height:2, background: sidebarOpen ? "transparent" : "#aaa", borderRadius:2, transition:"all 0.2s" }}/>
        <span style={{ display:"block", width:16, height:2, background: sidebarOpen ? "#fff" : "#aaa", borderRadius:2, transition:"all 0.2s", transform: sidebarOpen ? "rotate(-45deg) translate(3px,-3px)" : "none" }}/>
      </button>

      {/* ── Backdrop ── */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position:"fixed", inset:0, zIndex:65, background:"rgba(0,0,0,0.45)", backdropFilter:"blur(3px)" }}
        />
      )}

      {/* ── Sidebar panel ── */}
      <div style={{
        position: "fixed", top:0, right:0, bottom:0, width:260, zIndex:66,
        background: "rgba(7,7,16,0.99)", backdropFilter:"blur(24px)",
        borderLeft: "1px solid rgba(255,255,255,0.08)",
        display: "flex", flexDirection:"column",
        transform: sidebarOpen ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.24s cubic-bezier(0.4,0,0.2,1)",
      }}>
        {/* Wordmark */}
        <div style={{ padding:"28px 24px 20px", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ fontSize:26, fontWeight:400, fontFamily:"'Arexa','Space Mono',monospace", color:"#f97316", letterSpacing:2 }}>SUBLIME</div>
          <div style={{ fontSize:10, color:"#333", marginTop:4, fontFamily:"'Space Mono',monospace", letterSpacing:1 }}>PERSONAL FITNESS OS</div>
        </div>

        {/* Nav items */}
        <div style={{ flex:1, overflowY:"auto", paddingTop:8 }}>
          {navItems.map(v => {
            if (v === "Watch Sync") {
              const syncing = watchSyncStatus.status === "syncing";
              const failed = watchSyncStatus.status === "error";
              const message = watchSyncStatus.message || "Ready to sync Samsung Health";
              return (
                <div key={v} style={{
                  margin:"10px 14px 12px",
                  padding:"13px",
                  borderRadius:10,
                  background:"rgba(52,211,153,0.08)",
                  border:"1px solid rgba(52,211,153,0.18)",
                }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:9 }}>
                    <div>
                      <div style={{ fontSize:10, color:"#8dddb5", fontFamily:"'Space Mono',monospace", letterSpacing:1.2, textTransform:"uppercase" }}>Watch Sync</div>
                      <div style={{ marginTop:3, fontSize:12, color: failed ? "#fca5a5" : syncing ? "#fdba74" : "#b7f7d4", lineHeight:1.35 }}>{message}</div>
                    </div>
                    <span style={{
                      width:8,
                      height:8,
                      borderRadius:"50%",
                      background: failed ? "#ef4444" : syncing ? "#f97316" : "#34d399",
                      opacity: syncing || failed ? 1 : 0.65,
                      display:"inline-block",
                      flexShrink:0,
                    }}/>
                  </div>
                  <button onClick={requestWatchSync} disabled={syncing} style={{
                    width:"100%",
                    border:"1px solid rgba(249,115,22,0.4)",
                    borderRadius:8,
                    cursor: syncing ? "default" : "pointer",
                    background: syncing ? "rgba(249,115,22,0.10)" : "rgba(249,115,22,0.18)",
                    color: syncing ? "#fdba74" : "#f97316",
                    padding:"9px 12px",
                    fontSize:13,
                    fontWeight:700,
                    fontFamily:"'DM Sans',sans-serif",
                  }}>
                    {syncing ? "Syncing..." : "Sync Now"}
                  </button>
                  <button onClick={openWatchDiagnostics} style={{
                    width:"100%",
                    marginTop:8,
                    border:"1px solid rgba(52,211,153,0.25)",
                    borderRadius:8,
                    cursor:"pointer",
                    background:"rgba(52,211,153,0.08)",
                    color:"#8dddb5",
                    padding:"8px 12px",
                    fontSize:12,
                    fontWeight:700,
                    fontFamily:"'DM Sans',sans-serif",
                  }}>
                    Diagnostics
                  </button>
                </div>
              );
            }

            const hasAlerts = v === "Admin" && systemAlerts.some(a => !a.read && (a.severity === "critical" || a.severity === "warning"));
            return (
              <button key={v} onClick={() => { setView(v); setSidebarOpen(false); }} style={{
                width:"100%", border:"none", cursor:"pointer", background:"transparent",
                padding:"13px 24px", fontSize:14,
                fontWeight: view === v ? 600 : 400,
                fontFamily:"'DM Sans',sans-serif", textAlign:"left",
                color: view === v ? "#f97316" : "#666",
                borderLeft: "3px solid " + (view === v ? "#f97316" : "transparent"),
                display:"flex", alignItems:"center", justifyContent:"space-between",
                transition:"color 0.1s",
              }}>
                {v}
                {hasAlerts && <span style={{ width:7, height:7, borderRadius:"50%", background:"#ef4444", display:"inline-block", flexShrink:0 }}/>}
              </button>
            );
          })}
        </div>

        {/* Current view label at bottom */}
        <div style={{ padding:"16px 24px", borderTop:"1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ fontSize:9, color:"#333", fontFamily:"'Space Mono',monospace", letterSpacing:1.2, marginBottom:4 }}>VIEWING</div>
          <div style={{ fontSize:13, color:"#f97316", fontWeight:600 }}>{view}</div>
          <div style={{ display:"flex", flexDirection:"column", gap:3, marginTop:12, paddingTop:12, borderTop:"1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ fontSize:9, color:"#333", fontFamily:"'Space Mono',monospace", letterSpacing:1.2 }}>OWNER</div>
            <div style={{ fontSize:12, color:"#888" }}>{APP_INFO.ownerName}</div>
            <div style={{ fontSize:10, color:"#555", fontFamily:"'Space Mono',monospace" }}>
              web v{APP_INFO.webVersion}{nativeInfo?.version ? ` · android v${nativeInfo.version}` : ""}
            </div>
          </div>
        </div>
      </div>

      {/* ── System alert banner (critical/warning only) ── */}
      {systemAlerts.some(a => !a.read && a.severity === "critical") && (
        <div style={{
          background: "rgba(239,68,68,0.1)", borderBottom: "1px solid rgba(239,68,68,0.25)",
          padding: "9px 20px", fontSize: 13, color: "#f87171",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
        }}>
          <span>⚠ System alert — something needs your attention</span>
          <button onClick={() => setView("Admin")} style={{
            background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)",
            color: "#f87171", borderRadius: 6, padding: "3px 12px", fontSize: 12,
            cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
          }}>
            View in Admin
          </button>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: "calc(24px + env(safe-area-inset-bottom))", left: "50%", transform: "translateX(-50%)",
          background: "#0f1a0f", border: "1px solid rgba(52,211,153,0.4)",
          color: "#34d399", padding: "10px 22px", borderRadius: 12,
          fontSize: 14, fontWeight: 600, zIndex: 999,
          boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
        }}>
          {toast}
        </div>
      )}

      {/* ── View Router ── */}
      {watchDiagnostics.open && (() => {
        const data = watchDiagnostics.data || {};
        const latest = data.latestSyncDiagnostic || {};
        const nativeDiag = latest.diagnostics || {};
        const counts = nativeDiag.healthCountsByDate?.[todayDate] || {};
        const readStatus = nativeDiag.readStatus || {};
        const todayExerciseSessions = nativeDiag.exerciseSessionCountsByDate?.[todayDate] || counts.exerciseSessions || 0;
        const todayHasDailySignals = Boolean((counts.steps || 0) || (counts.heartRate || 0) || (counts.dailyEnergy || 0));
        const batteryRestrictionHint = todayExerciseSessions === 0 && todayHasDailySignals;
        const verdict = watchDiagnostics.error
          ? watchDiagnostics.error
          : batteryRestrictionHint
            ? "Today has daily health data but zero workout sessions. Check Health Sync battery restriction/unrestricted battery access, then resync."
            : (data.workouts?.length ? "Workout data exists in Fitlog for today." : "No workout records are stored for today yet.");
        const rows = [
          ["Date checked", data.date || todayDate],
          ["Last diagnostic", latest.syncedAt ? new Date(latest.syncedAt).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg" }) : "None"],
          ["ExerciseSession records", readStatus.ExerciseSession?.records ?? nativeDiag.exerciseSessionRecords ?? "-"],
          ["Today workout sessions", todayExerciseSessions],
          ["Yesterday workout sessions", nativeDiag.exerciseSessionCountsByDate?.[nativeDiag.auditDates?.[0]] ?? "-"],
          ["Day before workout sessions", nativeDiag.exerciseSessionCountsByDate?.[nativeDiag.auditDates?.[1]] ?? "-"],
          ["ExerciseSession error", readStatus.ExerciseSession?.error || nativeDiag.exerciseSessionReadError || "None"],
          ["Workout rows built", nativeDiag.workoutRowsBuilt ?? "-"],
          ["Overall step days", data.steps?.length ?? "-"],
          ["Heart-rate days", data.heartRate?.length ?? "-"],
          ["Daily energy rows", data.dailyEnergy?.length ?? "-"],
          ["Latest DB workout", data.latestSync?.workout ? `${data.latestSync.workout.date} - ${data.latestSync.workout.type}` : "None"],
          ["Step scope", nativeDiag.stepScope || "overall daily totals, not per exercise"],
          ["Missing required permissions", nativeDiag.permissions?.missingRequired?.join(", ") || "None"],
          ["Missing optional permissions", nativeDiag.permissions?.missingOptional?.join(", ") || "None"],
        ];
        return (
          <div onClick={closeWatchDiagnostics} style={{ position:"fixed", inset:0, zIndex:1000, background:"rgba(0,0,0,0.72)", display:"grid", placeItems:"center", padding:16 }}>
            <div onClick={event => event.stopPropagation()} style={{ width:"min(620px,100%)", maxHeight:"88vh", overflowY:"auto", background:"#07110d", border:"1px solid rgba(52,211,153,0.25)", borderRadius:16, boxShadow:"0 20px 80px rgba(0,0,0,0.55)" }}>
              <div style={{ padding:"16px 18px", borderBottom:"1px solid rgba(52,211,153,0.12)", display:"flex", justifyContent:"space-between", gap:12, alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontSize:11, color:"#8dddb5", fontFamily:"'Space Mono',monospace", letterSpacing:1.2, textTransform:"uppercase" }}>Watch Sync Diagnostics</div>
                  <div style={{ marginTop:6, color:"#d1fae5", fontWeight:700 }}>{watchDiagnostics.loading ? "Reading latest diagnostic..." : verdict}</div>
                </div>
                <button onClick={closeWatchDiagnostics} style={{ background:"rgba(255,255,255,0.05)", color:"#94a3b8", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"6px 10px", cursor:"pointer" }}>Close</button>
              </div>
              <div style={{ padding:18 }}>
                {watchDiagnostics.loading ? (
                  <div style={{ color:"#94a3b8", fontSize:13 }}>Loading diagnostics...</div>
                ) : (
                  <>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:8 }}>
                      {rows.map(([label, value]) => (
                        <div key={label} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, padding:"10px 12px" }}>
                          <div style={{ fontSize:9, color:"#6ee7b7", fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:0.8 }}>{label}</div>
                          <div style={{ marginTop:4, color:"#e2e8f0", fontSize:13, wordBreak:"break-word" }}>{String(value)}</div>
                        </div>
                      ))}
                    </div>
                    {nativeDiag.warnings?.length > 0 && (
                      <div style={{ marginTop:12, color:"#fbbf24", fontSize:12 }}>Warnings: {nativeDiag.warnings.join(", ")}</div>
                    )}
                    {batteryRestrictionHint && (
                      <div style={{ marginTop:12, color:"#fbbf24", fontSize:12, lineHeight:1.5 }}>
                        Health Sync can be blocked when Android battery saving restricts background activity. Set Health Sync to unrestricted battery usage, open Health Sync, run a manual sync, then sync Sublime again.
                      </div>
                    )}
                    {watchSyncStatus.summary && (
                      <details style={{ marginTop:14, color:"#94a3b8", fontSize:12 }}>
                        <summary style={{ cursor:"pointer", color:"#8dddb5" }}>Last in-app sync summary</summary>
                        <pre style={{ whiteSpace:"pre-wrap", wordBreak:"break-word", background:"rgba(0,0,0,0.25)", borderRadius:10, padding:12 }}>{JSON.stringify(watchSyncStatus.summary, null, 2)}</pre>
                      </details>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {saveStatus.state !== "idle" && (
        <div style={{
          position:"fixed", left:16, bottom:16, zIndex:998, maxWidth:420,
          padding:"10px 14px", borderRadius:10, fontSize:12,
          background:saveStatus.state==="failed"?"#220d12":"#0c1712",
          color:saveStatus.state==="failed"?"#f87171":"#4ade80",
          border:`1px solid ${saveStatus.state==="failed"?"rgba(248,113,113,0.35)":"rgba(74,222,128,0.3)"}`,
          boxShadow:"0 4px 20px rgba(0,0,0,0.45)",
        }}>
          <div style={{ fontWeight:700 }}>
            {saveStatus.state==="saving" ? `Saving ${saveStatus.category}...` :
             saveStatus.state==="saved" ? `${saveStatus.category} saved to Supabase` :
             `${saveStatus.category || "Data"} was not saved`}
          </div>
          {saveStatus.error && <div style={{ marginTop:3,color:"#fca5a5" }}>{saveStatus.error}</div>}
          {saveStatus.state==="failed" && (
            <button onClick={retryFailedWrites} style={{ marginTop:8,padding:"5px 10px",borderRadius:6,cursor:"pointer",background:"rgba(248,113,113,0.1)",color:"#fca5a5",border:"1px solid rgba(248,113,113,0.3)" }}>
              Retry save
            </button>
          )}
        </div>
      )}

      <main className="main-pad" style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px" }}>
        <ErrorBoundary onError={addLog} onNavigate={setView}>
          {dataLoading ? (
            <div style={{ minHeight:"70vh", display:"grid", placeItems:"center", textAlign:"center" }}>
              <div>
                <div style={{ width:34, height:34, borderRadius:"50%", border:"3px solid rgba(249,115,22,0.15)", borderTopColor:"#f97316", margin:"0 auto 16px", animation:"state-spin 0.8s linear infinite" }}/>
                <div style={{ fontSize:15, fontWeight:600, color:"#ddd" }}>Loading your latest data...</div>
                <div style={{ fontSize:12, color:"#666", marginTop:5 }}>Your progress will appear as soon as cloud sync finishes.</div>
                <style>{`@keyframes state-spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            </div>
          ) : dataLoadError ? (
            <div style={{ minHeight:"70vh", display:"grid", placeItems:"center", textAlign:"center" }}>
              <div style={{ maxWidth:420, padding:"24px", background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:14 }}>
                <div style={{ fontSize:15, fontWeight:600, color:"#f87171" }}>Your data could not be loaded</div>
                <div style={{ fontSize:12, color:"#999", margin:"8px 0 16px" }}>{dataLoadError}</div>
                <button onClick={() => window.location.reload()} style={{ background:"rgba(249,115,22,0.12)", color:"#f97316", border:"1px solid rgba(249,115,22,0.3)", borderRadius:8, padding:"8px 16px", cursor:"pointer", fontWeight:600 }}>Retry</button>
              </div>
            </div>
          ) : (<>
          {view === "Dashboard" && <Dashboard {...shared} />}
          {view === "Meal Log"  && <MealLog   {...shared} />}
          {view === "Supply"    && <Supply    logs={shared.logs} showToast={shared.showToast} />}
          {view === "History"   && <History   weightLog={shared.weightLog} fitnessGoals={shared.fitnessGoals} />}
          {view === "Progress"  && <Progress  {...shared} />}
          {view === "Fitness"   && <Fitness   workouts={shared.workouts} saveWorkouts={shared.saveWorkouts} deleteFitnessWorkout={shared.deleteFitnessWorkout} fitnessGoals={shared.fitnessGoals} weightLog={shared.weightLog} healthConnect={shared.healthConnect} showToast={shared.showToast} />}
          {view === "Foods"     && <Foods     customFoods={shared.customFoods} saveCustomFoods={shared.saveCustomFoods} logs={shared.logs} saveLogs={shared.saveLogs} showToast={shared.showToast} />}
          {view === "Profile"   && <Profile   fitnessGoals={shared.fitnessGoals} saveFitnessGoals={shared.saveFitnessGoals} macroGoals={shared.macroGoals} saveMacroGoals={shared.saveMacroGoals} weightLog={shared.weightLog} showToast={shared.showToast} />}
          {view === "Admin"     && <Admin     {...shared} systemAlerts={systemAlerts} dismissAlert={dismissAlert} clearAlerts={clearAlerts} />}
          </>)}
        </ErrorBoundary>
      </main>
    </div>
  );
}
